import { createContext, useState, useEffect, useContext, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { API_CONFIG, AUTH_CONFIG, ENDPOINTS } from "../config";

const AuthContext = createContext();

const getStoredSession = () => ({
  token: localStorage.getItem(AUTH_CONFIG.TOKEN_KEY),
  refreshToken: localStorage.getItem(AUTH_CONFIG.REFRESH_TOKEN_KEY),
  expiresAt: localStorage.getItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY),
});

const persistSession = ({ token, refreshToken, expiresAt }) => {
  if (token) {
    localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, token);
  } else {
    localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
  }

  if (refreshToken) {
    localStorage.setItem(AUTH_CONFIG.REFRESH_TOKEN_KEY, refreshToken);
  } else {
    localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
  }

  if (expiresAt) {
    localStorage.setItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY, expiresAt);
  } else {
    localStorage.removeItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY);
  }
};

const clearStoredSession = () => {
  localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
  localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_CONFIG.TOKEN_EXPIRY_KEY);
};

const getTokenExpiryMs = (token, expiresAt) => {
  if (expiresAt) {
    const parsed = Date.parse(expiresAt);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  if (token) {
    try {
      const decoded = jwtDecode(token);
      if (decoded?.exp) {
        return decoded.exp * 1000;
      }
    } catch (error) {
      console.error("Failed to decode token expiry:", error);
    }
  }

  return null;
};

const dispatchSessionUpdated = (session) => {
  window.dispatchEvent(
    new CustomEvent("auth-token-updated", {
      detail: session,
    })
  );
};

const dispatchLoggedOut = () => {
  window.dispatchEvent(new CustomEvent("auth-logged-out"));
};

const refreshSessionRequest = async (refreshToken) => {
  const response = await axios.post(
    `${API_CONFIG.BASE_URL}${ENDPOINTS.AUTH.REFRESH}`,
    { refreshToken }
  );

  return {
    token: response.data.token,
    refreshToken: response.data.refreshToken,
    expiresAt: response.data.expiresAt,
  };
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [pendingGiftCode, setPendingGiftCode] = useState(null);
  const refreshTimerRef = useRef(null);
  const refreshInFlightRef = useRef(false);

  const fetchUserProfile = async (userId, authToken) => {
    try {
      const response = await axios.get(
        `${API_CONFIG.BASE_URL}/users/${userId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const decodedToken = jwtDecode(authToken);
      const completeUser = {
        ...decodedToken,
        ...response.data,
        id: decodedToken.id,
        role: decodedToken.role,
      };

      setUser(completeUser);
      return completeUser;
    } catch (error) {
      console.error("Failed to fetch complete user profile:", error);
      const fallbackUser = jwtDecode(authToken);
      setUser(fallbackUser);
      return fallbackUser;
    }
  };

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const scheduleTokenRefresh = ({ token: nextToken, expiresAt } = {}) => {
    clearRefreshTimer();
    const expiryMs = getTokenExpiryMs(nextToken, expiresAt);
    if (!expiryMs) {
      return;
    }

    const leadTimeMs = 5 * 60 * 1000;
    const now = Date.now();
    const delayMs = Math.max(expiryMs - now - leadTimeMs, 0);

    if (delayMs === 0) {
      refreshSessionSilently();
      return;
    }

    refreshTimerRef.current = setTimeout(() => {
      refreshSessionSilently();
    }, delayMs);
  };

  const refreshSessionSilently = async () => {
    if (refreshInFlightRef.current) {
      return;
    }

    const { refreshToken } = getStoredSession();
    if (!refreshToken) {
      return;
    }

    refreshInFlightRef.current = true;
    try {
      const refreshedSession = await refreshSessionRequest(refreshToken);
      await applySession(refreshedSession);
    } catch (error) {
      console.error("Failed to refresh session silently:", error);
      const status = error?.response?.status;
      if ([400, 401, 403].includes(status)) {
        await logout();
      } else {
        scheduleTokenRefresh(getStoredSession());
      }
    } finally {
      refreshInFlightRef.current = false;
    }
  };

  const applySession = async ({ token: nextToken, refreshToken, expiresAt }) => {
    if (!nextToken) {
      setToken(null);
      setUser(null);
      clearStoredSession();
      clearRefreshTimer();
      return null;
    }

    persistSession({ token: nextToken, refreshToken, expiresAt });
    setToken(nextToken);

    const decodedToken = jwtDecode(nextToken);
    setUser(decodedToken);
    fetchUserProfile(decodedToken.id, nextToken);
    scheduleTokenRefresh({ token: nextToken, expiresAt });
    return decodedToken;
  };

  const login = async ({ token: nextToken, refreshToken, expiresAt }) => {
    await applySession({ token: nextToken, refreshToken, expiresAt });
  };

  const logout = async ({ broadcast = true } = {}) => {
    const { refreshToken } = getStoredSession();

    if (refreshToken) {
      try {
        await axios.post(`${API_CONFIG.BASE_URL}${ENDPOINTS.AUTH.LOGOUT}`, {
          refreshToken,
        });
      } catch (error) {
        console.error("Failed to notify backend during logout:", error);
      }
    }

    setUser(null);
    setToken(null);
    clearStoredSession();
    clearRefreshTimer();

    if (broadcast) {
      dispatchLoggedOut();
    }
  };

  const initializeAuth = async () => {
    const storedSession = getStoredSession();

    if (!storedSession.token && !storedSession.refreshToken) {
      setInitialized(true);
      return;
    }

    if (storedSession.token) {
      try {
        const decoded = jwtDecode(storedSession.token);
        const currentTime = Date.now() / 1000;

        if (!decoded.exp || decoded.exp > currentTime) {
          setToken(storedSession.token);
          setUser(decoded);
          await fetchUserProfile(decoded.id, storedSession.token);
          scheduleTokenRefresh(storedSession);
          setInitialized(true);
          return;
        }
      } catch (error) {
        console.error("Failed to decode stored token:", error);
      }
    }

    if (storedSession.refreshToken) {
      try {
        const refreshedSession = await refreshSessionRequest(
          storedSession.refreshToken
        );
        await applySession(refreshedSession);
        setInitialized(true);
        return;
      } catch (error) {
        console.error("Failed to refresh session during init:", error);
      }
    }

    clearStoredSession();
    setToken(null);
    setUser(null);
    setInitialized(true);
  };

  useEffect(() => {
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      const storedSession = getStoredSession();
      const expiryMs = getTokenExpiryMs(
        storedSession.token,
        storedSession.expiresAt
      );
      if (expiryMs && expiryMs - Date.now() <= 2 * 60 * 1000) {
        refreshSessionSilently();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleSessionUpdated = async (event) => {
      const nextSession = event.detail || {};
      try {
        await applySession(nextSession);
        scheduleTokenRefresh(nextSession);
      } catch (error) {
        console.error("Failed to apply updated session:", error);
        clearStoredSession();
        setToken(null);
        setUser(null);
        clearRefreshTimer();
      }
    };

    const handleLoggedOut = () => {
      clearStoredSession();
      setToken(null);
      setUser(null);
      clearRefreshTimer();
    };

    window.addEventListener("auth-token-updated", handleSessionUpdated);
    window.addEventListener("auth-logged-out", handleLoggedOut);

    return () => {
      window.removeEventListener("auth-token-updated", handleSessionUpdated);
      window.removeEventListener("auth-logged-out", handleLoggedOut);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPendingGift = (giftCode) => {
    setPendingGiftCode(giftCode);
    localStorage.setItem("pendingGiftCode", giftCode);
  };

  const clearPendingGift = () => {
    setPendingGiftCode(null);
    localStorage.removeItem("pendingGiftCode");
  };

  useEffect(() => {
    const storedGiftCode = localStorage.getItem("pendingGiftCode");
    if (storedGiftCode) {
      setPendingGiftCode(storedGiftCode);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        initialized,
        pendingGiftCode,
        setPendingGift,
        clearPendingGift,
        dispatchSessionUpdated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthContext, AuthProvider, useAuth };
